import { axios_tenant } from "../../../../axios";
import { toast } from "react-toastify";
import { SECTION_SCHEMA_MAPPING } from "./constants";
import transformClientResponse from "./transformClientResponse";

const ONE_TO_ONE_SECTIONS = [
  "clientDetails",
  "billingIdsArrangement"
];

const mapOneToOneData = (section, formData) => {
  switch (section) {
    case "clientDetails":
      return {
        clientLegalName: formData.clientName || "",
        sourceOfClientName: formData.sourceOfClientName || "",
        usptoCustomerNumber: formData.usptoCustomerNumber || "",
        address: formData.address || "",
        phoneNumber: formData.phone || "",
        entityStatus: formData.entityStatus || "",
        sourceOfEntityStatus: formData.entityStatusSource || "",
        incorporationCountry: formData.corporationCountry || "",
        incorporationState: formData.corporationState || "",
        poaExecuted: formData.executedPoa || "",
        poaLocation: formData.executedPoaLocation || "",
        assignmentCountry: formData.assignmentCountry || "",
        assignmentState: formData.assignmentState || "",
        signatory: formData.signatory || "",
        signatoryTitle: formData.signatoryTitle || "",
        lastUpdatedOn: new Date().toISOString(),
        documentUrls: formData.documentUrls || [],
      };
    case "additionalInfo":
       return {
         comments: typeof formData === "string" ? formData : (formData.comments || ""),
       };
    case "billingIdsArrangement":
       return {
         idsBillingArrangement: formData?.billingArrangement || "",
         notes: formData?.notes || ""
       };
    default:
      return formData;
  }
};

const mapFieldNames = (section, field) => {
  const mapping = {
    inventors: {
      prefix: "prefix",
      firstName: "firstName",
      middleName: "middleName",
      lastName: "lastName",
      suffix: "suffix",
      nationality: "nationality",
      citizenship: "citizenship",
      address1: "address1",
      address2: "address2",
      address3: "address3",
      address4: "address4",
      city: "city",
      state: "state",
      country: "country",
      verifiedDate: "verifiedDate",
      email: "emailId",
      notes: "notes",
    },
    correspondence: {
      correspondenceType: "correspondenceType",
      name: "name",
      email: "emailId",
      phone: "phoneNumber",
      familyNo: "familyNo",
      notes: "notes"
    },
    paralegalInstructions: { paralegalAction: "action", wsgrResponsible: "wsgrResponsible", familyNos: "matters", notes: "notes" },
    ppsInstructions: { ppsAction: "action", wsgrResponsible: "wsgrResponsible", familyNos: "matters", notes: "notes" },
    recurringActions: { recurringAction: "action", frequency: "frequency", nextActionDate: "nextActionDate" },
    statusByFamily: { wsgrResponsible: "wsgrHandles", familyNos: "familyNo", firmName: "prosecutingFirm" },
    foreignAssociates: { country: "country", associateName: "associateName", associateEmails: "emailId" }, // Note: emails mapped as string if 1
    licensedFamilies: { partyRelationship: "partyRelationship", familyNo: "familyNo", licensedTo: "licensedTo", contacts: "contactName" }, // specialized handling might be needed
    attorneyPreferences: { attorneyName: "agentAttorneyName", familyNo: "familyNo", notes: "notes" },
    annuties: { familyNo: "familyNo", paidBy: "paidBy", notes: "notes" },
    billingInstructions: { instructions: "instruction", advancedInvocingRequired: "advanceInvoiceRequired", notes: "notes" },
    billingInvoicingRecipients: { recpientName: "name", email: "emailId", phone: "phoneNumber", notes: "notes" },
    clientPreferences: { ptaReview: "ptaReview", receivedBy: "receivedBy", preferenceDate: "preferenceDate", notes: "notes" },
    additionalInfo: {comment: "comment", userId: "userId"},
  };
  return mapping[section]?.[field] || field;
};

const generateBulkChanges = (section, newData, originalData) => {
  const modelName = SECTION_SCHEMA_MAPPING[section];
  if (!modelName) return [];

  const changes = [];
  const oldMap = new Map((originalData || []).map(item => [String(item.id), item]));

  const sectionIdMap = {
    inventors: "inventorId",
    foreignAssociates: "foreignAssociateId",
    licensedFamilies: "licensedFamilyId",
    correspondence: "correspondenceId",
    paralegalInstructions: "paralegalInstructionId",
    ppsInstructions: "ppsInstructionId",
    recurringActions: "recurringActionId",
    statusByFamily: "shadowingStatusFamilyId",
    attorneyPreferences: "agentAttorneyPreferenceId",
    annuties: "annuityId",
    billingInstructions: "billingInstructionId",
    billingInvoicingRecipients: "billingInvoicingRecipientId"
  };

  const idField = sectionIdMap[section] || "id";

  const normalizeValue = (field, value) => {
    // Normalize "empty" values
    if (value === undefined) return null;
    if (value === null) return null;
    if (typeof value === "string") {
      if (value === "Invalid date") return null;
      if (value.trim() === "") return null;
      return value;
    }
    if (Array.isArray(value)) {
      return value.length === 0 ? null : value;
    }
    return value;
  };

  (newData || []).forEach(newItem => {
    const isNew = !oldMap.has(String(newItem.id));
    const action = isNew ? "CREATE" : "UPDATE";
    const oldItem = isNew ? {} : (oldMap.get(String(newItem.id)) || {});

    // For CREATE: skip entirely if user hasn’t filled anything meaningful
    if (action === "CREATE") {
      // Foreign Associates: require a fully valid selection (country + associate + at least 1 email)
      if (section === "foreignAssociates") {
        const normalizeEmailsList = (v) => {
          if (!v) return [];
          const rawList = Array.isArray(v) ? v : [v];
          return rawList
            .flatMap((e) => String(e).split(","))
            .map((e) => e.trim())
            .filter(Boolean);
        };

        const emails = normalizeEmailsList(newItem.associateEmails);
        if (!newItem.country || !newItem.associateName || emails.length === 0) {
          return;
        }
      }

      const hasAnyNonEmptyField = Object.keys(newItem).some((field) => {
        if (field === "id") return false;
        let candidate = newItem[field];
        if (section === "foreignAssociates" && field === "associateEmails") {
          candidate = Array.isArray(candidate) ? candidate.join(", ") : candidate;
        }
        const normalized = normalizeValue(field, candidate);
        return normalized !== null && normalized !== undefined && normalized !== "";
      });
      if (!hasAnyNonEmptyField) {
        return;
      }
    }

    Object.keys(newItem).forEach(field => {
       if (field === "id") return;

       let newVal = newItem[field];
       let oldVal = oldItem[field];

       // Foreign Associates: backend stores a single emailId string, UI stores an array
       if (section === "foreignAssociates" && field === "associateEmails") {
          const normalizeEmails = (v) => {
            if (!v) return "";
            const rawList = Array.isArray(v) ? v : [v];
            return rawList
              .flatMap((e) => String(e).split(","))
              .map((e) => e.trim())
              .filter(Boolean)
              .join(", ");
          };
          newVal = normalizeEmails(newVal);
          oldVal = normalizeEmails(oldVal);
       }

       newVal = normalizeValue(field, newVal);
       oldVal = normalizeValue(field, oldVal);

       // Handle specialized nested fields
       if (newVal !== oldVal) {
          const apiField = mapFieldNames(section, field);
          // For CREATE: only send fields that were actually provided (avoid null-only creates)
          if (action === "CREATE" && newVal === null) return;

          changes.push({
             section_name: modelName,
             model_name: modelName,
             action_type: action,
             record_id: String(newItem.id), 
             field_name: apiField,
             old_value: oldVal,
             new_value: newVal
          });
       }
    });
  });

  // Handle Deletions
  (originalData || []).forEach(oldItem => {
    const stillExists = (newData || []).some(newItem => String(newItem.id) === String(oldItem.id));
    if (!stillExists) {
      changes.push({
        section_name: modelName,
        model_name: modelName,
        action_type: "DELETE",
        record_id: String(oldItem.id)
      });
    }
  });

  return changes;
};

export const handleSave = async (dispatch, section, formData, clientId, originalData, options = {}) => {
  const {
    showSuccessToast = true,
    showErrorToast = true,
    successMessage = "Changes saved successfully.",
    errorMessage = "Failed to save changes.",
  } = options;

  if (!clientId || clientId === "new") {
     const message = "Cannot save section: Profile must be created first";
     console.error(message);
     if (showErrorToast) {
       toast.error(message);
     }
     return { status: "error" };
  }

  try {
    let response = null;
    if (ONE_TO_ONE_SECTIONS.includes(section)) {
      const mappedData = mapOneToOneData(section, formData);
      let payload;
      
      if (section === "clientDetails") {
         payload = {
            clientName: formData.clientName || "",
            clientNumber: formData.clientNumber || "",
            ...mappedData
         };
      } else {
         payload = {
             sections: [{
               sectionId: SECTION_SCHEMA_MAPPING[section],
               data: mappedData
             }]
         };
      }
      
      response = await axios_tenant.put(`/kot/client-profiles/${clientId}`, payload);
      console.log("1-to-1 save successful:", response.data);
    } else {
      // 1-to-N uses bulk-update
      const changes = generateBulkChanges(section, formData, originalData);
      if (changes.length > 0) {
         response = await axios_tenant.put(`/kot/client-profiles/${clientId}/bulk-update`, { changes });
         console.log("Bulk update successful:", response.data);
      } else {
         console.log("No changes detected for section:", section);
      }
    }

    // Prefer server as source of truth to prevent temp-id drift and repeated diffs
    const hasProfileShape = response?.data && Array.isArray(response.data.sections);
    if (hasProfileShape) {
      dispatch({
        type: "SET_FULL_CLIENT",
        payload: transformClientResponse(response.data),
      });
      // Note: the global kot-form-saved event is intentionally NOT emitted here.
      // Callers that batch multiple handleSave calls (Billing, ClientDetails)
      // must only clear the unsaved-changes guard once ALL sub-saves succeed,
      // so each component calls markSaved() itself at that aggregate point.
      if (showSuccessToast) {
        toast.success(successMessage);
      }
      return { status: "saved", data: response.data };
    }

    // If we didn't call the API (e.g., no changes), don't mutate Redux.
    if (!response) {
      return { status: "no_changes" };
    }

    // Fallback: keep local state (should be rare)
    dispatch({
      type: "UPDATE_SECTION",
      payload: { section, value: formData },
    });

    if (showSuccessToast) {
      toast.success(successMessage);
    }

    return { status: "saved", data: response.data };
  } catch (error) {
    console.error("Save error:", error);
    if (showErrorToast) {
      toast.error(error?.response?.data?.message || error?.message || errorMessage);
    }
    return { status: "error", error };
  }
};
